<template>
  <div class="i-feedback">
    <div id="career-feedback-experience">
      <div class="background"><iNavbarSimple /> <iReportHero /></div>
      <div class="shade" />

      <div class="feedback-modal">
        <div
          v-if="!data.isSkippingFeedback"
          class="header"
        >
          <div class="left">
            <button
              v-if="data.hasStartedFeedback && data.activeJobIndex > 0"
              class="nav-button"
              @click="data.activeJobIndex--"
            >
              <img
                :src="iconBackButtonBlack"
                loading="lazy"
                alt="Close"
                class="icon"
              />
            </button>
          </div>
          <div class="right">
            <iBaseButton
              v-if="data.hasStartedFeedback && data.positiveFeedbackCount >= 5"
              v-bind="BUTTON_STYLE"
              text="Finish"
              width="fit-content"
              @clicked="feedbackCompleted"
            />
            <button
              v-else
              class="nav-button"
              @click="data.isSkippingFeedback = true"
            >
              <img
                :src="iconCloseButtonBlack"
                loading="lazy"
                alt="Close"
                class="icon"
              />
            </button>
          </div>
        </div>
        <div
          ref="mainContent"
          class="main"
        >
          <iFeedbackSkipWarning
            v-if="data.isSkippingFeedback"
            :content-area="mainContentArea"
          />
          <iFeedbackInfo
            v-else-if="!data.hasStartedFeedback"
            :content-area="mainContentArea"
            @started-feedback="data.hasStartedFeedback = true"
          />
          <iCareerSummary
            v-else-if="reportStore.suggestedJobs"
            :job="reportStore.suggestedJobs[data.activeJobIndex]"
            :content-area="mainContentArea"
          />
        </div>

        <div class="footer">
          <iFeedbackSkipFooter
            v-if="data.isSkippingFeedback"
            @back="data.isSkippingFeedback = false"
            @skip="feedbackCompleted"
          />
          <iBaseButton
            v-else-if="!data.hasStartedFeedback"
            class="button"
            v-bind="BUTTON_STYLE"
            width="240px"
            text="Continue"
            @clicked="data.hasStartedFeedback = true"
          />
          <iRater
            v-else
            @rating-selected="rateJob"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue"

// COMPOSABLES
import { useReportStore } from "@/stores/report"
import { useRouter } from "vue-router"

// HELPERS
import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"
import iNavbarSimple from "@/components/global/NavbarSimple.vue"
import iCareerSummary from "@/components/report/feedback/CareerSummary.vue"
import iFeedbackInfo from "@/components/report/feedback/FeedbackInfo.vue"
import iFeedbackSkipFooter from "@/components/report/feedback/FeedbackSkipFooter.vue"
import iFeedbackSkipWarning from "@/components/report/feedback/FeedbackSkipWarning.vue"
import iRater from "@/components/report/feedback/Rater.vue"
import iReportHero from "@/components/report/ReportHero.vue"

// ASSETS
import iconBackButtonBlack from "@/assets/buttons/back-button-black.svg"
import iconCloseButtonBlack from "@/assets/buttons/close-button-black.svg"

// TYPES
import type { Feedback } from "@/shared/types/UserJobFeedback"

// STYLES
import { astronaut500, white } from "@/styles/colors"

// CONSTANTS
const BUTTON_STYLE = {
  backgroundColor: astronaut500,
  borderRadius: "24px",
  fontWeight: "600",
  textColor: white,
  size: "small"
}

// STORES
const reportStore = useReportStore()

// ROUTER
const router = useRouter()

// REFS
const mainContent = ref<HTMLElement | null>(null)
const mainContentArea = ref<number>(0)

// DATA
const data = reactive({
  hasStartedFeedback: false,
  isSkippingFeedback: false,
  isFeedbackCompleted: false,
  activeJobIndex: 0,
  positiveFeedbackCount: 0
})

// METHODS
const calculateMainContentArea = () => {
  if (mainContent.value) {
    mainContentArea.value = mainContent.value.clientWidth * mainContent.value.clientHeight
  } else {
    mainContentArea.value = 0
  }
}

/**
 * Complete the feedback
 *
 * Reload the report and redirect the user
 */
const feedbackCompleted = () => {
  try {
    api.report.postFeedbackExperienceCompleted()
  } catch (error) {
    console.error("Error completing feeback:", error)
  }

  data.isFeedbackCompleted = true

  const reroutingFunction = () => {
    router.push({ name: GlobalVariables.urls.report.children.home.routeName })
  }

  reportStore.triggerLoading(
    reroutingFunction,
    20000,
    "Enhancing Your Career Matches!",
    "This could take up to a few minutes. Please do not refresh the page!"
  )
}

/**
 * Send user feedback to the backend
 *
 * If the user has rated all the suggested jobs, the feedback is completed
 * Otherwise, the next job is displayed
 */
const rateJob = (feedback: Feedback) => {
  if (feedback > 0) {
    data.positiveFeedbackCount++
  }

  const jobId = reportStore.suggestedJobs[data.activeJobIndex].id
  reportStore.postUserJobFeedback(jobId, {
    feedback,
    source: "initial"
  })

  data.activeJobIndex++

  if (data.activeJobIndex >= reportStore.suggestedJobs.length) {
    feedbackCompleted()
  } else {
    mainContent.value?.scrollTo({ top: 0, behavior: "smooth" })
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  calculateMainContentArea()
  window.addEventListener("resize", calculateMainContentArea)
  await reportStore.loadSuggestedJobs()

  const suggestedJobs = [...reportStore.suggestedJobs]

  // If there are no suggested jobs, handle the error by completing the feedback
  if (!suggestedJobs || suggestedJobs.length === 0) {
    feedbackCompleted()
  }

  // Find the first job that has not been rated (feedback is null) and set it as the active job
  data.activeJobIndex = suggestedJobs.findIndex((job) => job.feedback === null)

  // Set the positive feedback count to the number of jobs that have already been rated positively
  data.positiveFeedbackCount = suggestedJobs.filter((job) => job.feedback !== null && job.feedback > 0).length
})
</script>

<style lang="scss" scoped>
.i-feedback {
  #career-feedback-experience {
    display: flex;
    flex-direction: column;
    height: 100vh;

    .background {
      position: fixed;
      inset: 0;
      z-index: $z-index-background;
    }

    .shade {
      background-color: rgba($pickle-bluewood950, 0.96);
      position: fixed;
      inset: 0;
      z-index: $z-index-background + 100;
    }

    .feedback-modal {
      z-index: $z-index-modal;
      border-radius: 16px;
      background-color: white;
      height: min(650px, 95vh);
      width: min(800px, 90vw);
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;

      .header {
        @include flex($direction: row, $justify: space-between, $align: center);

        padding: 12px;

        .nav-button {
          background-color: $black50;
          border: none;
          cursor: pointer;
          padding: 4px;
          margin: 0;
          border-radius: 50%;

          &:hover {
            background-color: $black100;
          }

          .icon {
            height: 20px;
            width: 20px;
          }
        }
      }

      .main {
        @include flex($direction: column, $justify: flex-start, $align: center);

        flex-grow: 1;
        overflow-y: auto;
        padding: 12px 24px 24px;
      }

      .footer {
        @include flex($direction: column, $justify: center, $align: center);

        padding: 12px 24px;
        bottom: 0;
        width: 100%;
      }
    }
  }
}
</style>
